import { useEffect, useState } from "react";
import { Button } from "@repo/ui/primitives/button";
import { useHelperTourStore } from "@/store/helperTour.store";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import i18n from "i18next";

export function HelperTourOverlay() {
  const { t, ready } = useTranslation("helperTour", { useSuspense: false });
  const navigate = useNavigate();
  const { isOpen, steps, currentStepIndex, nextStep, prevStep, endTour, isCustomTour } = useHelperTourStore();
  const [targetRect, setTargetRect] = useState(null);
  const [activeModuleKey, setActiveModuleKey] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    void i18n.loadNamespaces("helperTour");
  }, [isOpen]);

  const handleEndTour = () => {
    endTour();
    if (isCustomTour) {
      navigate("/user-manual/patch-notes");
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setTargetRect(null);
      return;
    }

    const step = steps[currentStepIndex];
    if (!step) return;

    let attempts = 0;
    const findElement = () => {
      const element = document.querySelector(step.target);
      if (element) {
        setTargetRect(element.getBoundingClientRect());
        return true;
      }
      return false;
    };

    if (findElement()) return;

    const interval = setInterval(() => {
      attempts++;
      if (findElement() || attempts >= 20) {
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [isOpen, steps, currentStepIndex]);

  const step = steps[currentStepIndex];
  const hasPrev = currentStepIndex > 0;
  const isLast = currentStepIndex === steps.length - 1;

  // Sidebar ana modül adımında, tıklanan modül başlığına göre açıklamayı değiştirmek için
  useEffect(() => {
    if (!step || step.id !== "sidebar-tabs") {
      setActiveModuleKey(null);
      return;
    }

    const handleClick = (event) => {
      const target = event.target.closest("[data-helper-module]");
      if (!target) return;

      const moduleKey = target.getAttribute("data-helper-module");
      if (!moduleKey) return;

      // Navigasyonu engelle, sadece açıklama metnini değiştir
      event.preventDefault();
      event.stopPropagation();
      setActiveModuleKey(moduleKey);
    };

    document.addEventListener("click", handleClick, true);
    return () => {
      document.removeEventListener("click", handleClick, true);
    };
  }, [step]);

  // Basit pozisyonlama: highlight kutusunun yanına kartı koymayı deneriz, yer yoksa altına/üstüne alırız
  const cardStyle = (() => {
    if (!targetRect) {
      return {
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
      };
    }

    const margin = 16;
    const cardWidth = 320;
    const cardHeight = 220; // safe height estimate

    let left = targetRect.right + margin;
    let top = targetRect.top;

    // Eğer sağda yer yoksa, hedefin altına veya üstüne alıp yatayda ortalayalım
    if (left + cardWidth > window.innerWidth) {
      left = Math.min(
        Math.max(targetRect.left + (targetRect.width - cardWidth) / 2, margin),
        window.innerWidth - cardWidth - margin
      );

      // Dikey konumlandırma: hedefin altında yer yoksa üstüne koy
      if (targetRect.bottom + margin + cardHeight > window.innerHeight) {
        top = targetRect.top - cardHeight - margin;
      } else {
        top = targetRect.bottom + margin;
      }
    } else {
      // Sağ taraftayken ekranın üstüne veya altına taşmasını engelle
      top = Math.min(
        Math.max(top, margin),
        window.innerHeight - cardHeight - margin
      );
    }

    // Ekran dışına taşmayı garanti şekilde engellemek için sınır kontrolleri
    top = Math.min(Math.max(top, margin), window.innerHeight - cardHeight - margin);
    left = Math.min(Math.max(left, margin), window.innerWidth - cardWidth - margin);

    return {
      top,
      left,
    };
  })();

  if (!isOpen || !ready) return null;

  return (
    <div className="pointer-events-none fixed inset-0 z-[var(--ds-layer-transient)]">
      {/* Karanlık arka plan - hedef alanın etrafında 4 panel ile "boşluk" bırakarak */}
      {targetRect && (
        <>
          {/* Üst panel */}
          <div
            className="pointer-events-auto absolute left-0 right-0 bg-[var(--ds-overlay-backdrop)]"
            style={{ top: 0, height: targetRect.top }}
            onClick={handleEndTour}
          />
          {/* Alt panel */}
          <div
            className="pointer-events-auto absolute left-0 right-0 bg-[var(--ds-overlay-backdrop)]"
            style={{ top: targetRect.bottom, bottom: 0 }}
            onClick={handleEndTour}
          />
          {/* Sol panel */}
          <div
            className="pointer-events-auto absolute bg-[var(--ds-overlay-backdrop)]"
            style={{
              top: targetRect.top,
              left: 0,
              width: targetRect.left,
              height: targetRect.height,
            }}
            onClick={handleEndTour}
          />
          {/* Sağ panel */}
          <div
            className="pointer-events-auto absolute bg-[var(--ds-overlay-backdrop)]"
            style={{
              top: targetRect.top,
              left: targetRect.right,
              right: 0,
              height: targetRect.height,
            }}
            onClick={handleEndTour}
          />
        </>
      )}

      {/* Açıklama kartı */}
      <div
        className="pointer-events-auto absolute w-[320px] space-y-3 rounded-[var(--ds-radius-surface)] border border-border bg-card p-4 text-card-foreground shadow-[var(--shadow-popover)]"
        style={cardStyle}
      >
        <div className="space-y-1">
          <h2 className="text-base font-semibold text-foreground">
            {step ? t(step.titleKey) : ""}
          </h2>
          <p className="text-sm text-muted-foreground">
            {step
              ? step.id === "sidebar-tabs" && activeModuleKey
                ? t(`steps.sidebarTabs.modules.${activeModuleKey}.description`, {
                    defaultValue: "",
                  })
                : t(step.descriptionKey)
              : ""}
          </p>
        </div>

        <div className="flex items-center justify-between pt-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleEndTour}
            className="h-[var(--ds-density-control-height-sm)] px-2 text-xs text-muted-foreground hover:text-foreground"
          >
            {t("actions.finish", { defaultValue: "Finish" })}
          </Button>

          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={prevStep}
              disabled={!hasPrev}
              className="h-[var(--ds-density-control-height-sm)] px-2 text-xs"
            >
              {t("actions.prev", { defaultValue: "Back" })}
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={isLast ? handleEndTour : nextStep}
              className="h-[var(--ds-density-control-height-sm)] px-2 text-xs"
            >
              {isLast ? t("actions.finish", { defaultValue: "Finish" }) : t("actions.next", { defaultValue: "Next" })}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
